/**
 * The pogo body — the entire game feel lives in this file.
 *
 * Three spheres on one rigid axis: the body (`BODY_RADIUS`) at `pos`, the head
 * (`HEAD_RADIUS`, `HEAD_OFFSET` up the stick) and the tip (`TIP_RADIUS`) a
 * `STICK_LENGTH` below, all leaning together. The tip is swept *first* every
 * substep so a ledge-edge landing catches the edge instead of clipping the body
 * into the wall; the body is then swept along its own path — the guard against
 * crossing a thin platform inside one substep — and body and head are finally
 * depenetrated together, which is what stops you passing through pillars and
 * rails or burying your head in an overhang.
 *
 * The core loop is **bounce by default, plant on demand**. Land on anything you
 * can stand on (slope ≤ `SLIDE_ANGLE`) with the button *up* and you rebound like
 * a ball: `BOUNCE_RESTITUTION` of the impact speed comes back, so the harder you
 * fall the higher you go, and successive bounces decay until they hit the
 * `IDLE_BOUNCE_SPEED` floor — a perpetual little pogo on the spot that never
 * stops and never travels. Land with the button *held* and the stick **plants**
 * instead: the body stops dead and stays frozen, so you can aim with lean
 * knowing nothing will move you. Letting go fires the jump, from a tap at
 * `BASE_BOUNCE` to a full hold at `BASE_BOUNCE + CHARGE_GAIN`. Steeper than
 * `SLIDE_ANGLE` you cannot plant at all — funnels deflect you and keep feeding
 * you downhill, exactly as they always have.
 *
 * Everything runs on fixed `TUNING.FIXED_DT` substeps driven by
 * `core/loop.ts`; rendering interpolates between the previous and current
 * substep via {@link PogoBody.getPose}.
 *
 * Conventions:
 * - `pos` is the **body sphere centre**, not the feet.
 * - `stickUp` is the unit *up* axis of the stick (what `SkeletonPose.stickDir`
 *   wants). The tip is therefore `pos − stickUp · STICK_LENGTH`.
 * - Lean is stored **camera-relative** as `(leanX, leanZ)` in `[-1, 1]`, exactly
 *   as it travels on the wire (`PlayerState.l`). Combined with the yaw it went
 *   out with, {@link stickUpFromLean} reproduces the world-space stick axis, so
 *   a ghost leans identically to the player it mirrors.
 */

import { Quaternion, Vector3 } from 'three';
import type { LevelData, Material, Primitive, V3 } from '@shared/primitives';
import type { AnimState, PlayerState } from '@shared/protocol';
import {
  ANIM_AIRBORNE,
  ANIM_BOUNCE,
  ANIM_CHARGING,
  ANIM_FLAIL,
  roundState,
} from '@shared/protocol';
import type { SkeletonPose } from '../render/rigTypes';
import type { Broadphase } from './broadphase';
import { closestPoint, sweepSphere, type ContactHit, type SweepHit } from './collision';
import type { MoverSystem } from './moverSystem';

const DEG = Math.PI / 180;

/**
 * Every physics number, in one mutable object.
 *
 * Mutable on purpose: the debug overlay and the tuning phase edit these live and
 * the change has to take effect on the next substep. There is no second copy of
 * any of these values anywhere in the codebase.
 */
export const TUNING = {
  // --- integration ---
  /** Downward acceleration, m/s². */
  GRAVITY: -22,
  /** Physics substep length, seconds. */
  FIXED_DT: 1 / 120,
  /** Substeps a single rendered frame may run before time is dropped. */
  MAX_SUBSTEPS: 8,

  // --- body ---
  /** Radius of the collision sphere at the hips. */
  BODY_RADIUS: 0.45,
  /** Distance from the body centre down to the stick tip. */
  STICK_LENGTH: 1.15,
  /** Radius of the sphere swept along the stick tip's path. */
  TIP_RADIUS: 0.15,
  /**
   * Distance from the body centre up to the *second* collision sphere, the head.
   *
   * The rendered skeleton stands well above the hips, and for a long time the
   * only thing that collided up there was nothing at all: the body sphere ends
   * `BODY_RADIUS` above `pos` and everything the rig draws past that clipped
   * straight through overhangs, ceilings and the undersides of platforms while
   * the hips were still comfortably clear. Anything you can see has to collide.
   */
  HEAD_OFFSET: 0.55,
  /** Radius of the head sphere. Reaches `HEAD_OFFSET + HEAD_RADIUS` above `pos`. */
  HEAD_RADIUS: 0.3,

  // --- lean ---
  /** Maximum tilt of the stick from vertical, radians. */
  MAX_LEAN: 38 * DEG,
  /** Lean smoothing rate, 1/s. Slewed, never snapped — see `update`. */
  LEAN_RATE: 9,
  /** How much of the bounce direction comes from the stick rather than the surface. */
  LEAN_AUTHORITY: 0.75,
  /** The bounce direction is never allowed closer than this to the surface plane. */
  MIN_BOUNCE_DOT: 0.3,

  // --- bounce ---
  /** Bounce speed with no charge and no hard landing, m/s (idle apex ≈ 0.96 m). */
  BASE_BOUNCE: 6.5,
  /** Fraction of the *excess* impact speed that is handed back. See `resolveBounce`. */
  IMPACT_RETENTION: 0.35,
  /** Extra bounce speed at full charge, m/s. */
  CHARGE_GAIN: 7.5,
  /** Floor on any launch speed — a jump, or a deflection off a slide surface. */
  MIN_BOUNCE_SPEED: 4,
  /** Ceiling on a single bounce, before the material multiplier. */
  MAX_BOUNCE_SPEED: 18,
  /** Fraction of tangential velocity kept through a landing. */
  TANGENT_KEEP: 0.5,
  /** Contact cannot re-trigger a bounce for this long, seconds. */
  CONTACT_COOLDOWN: 0.05,
  /** Spring compression decays at this rate, 1/s. */
  COMPRESSION_DECAY: 6,
  /** Below this airborne time the player counts as grounded. */
  GROUND_TIME: 0.15,

  // --- auto-bounce ---
  /** Fraction of the impact speed an un-charged landing hands straight back. */
  BOUNCE_RESTITUTION: 0.55,
  /**
   * Floor on an auto-bounce, m/s (apex ≈ 0.15 m). Restitution alone would damp
   * the body to a standstill; this floor is what turns the decay into a
   * perpetual little pogo on the spot instead.
   */
  IDLE_BOUNCE_SPEED: 2.6,
  /**
   * Impact speed below which a landing fires no `onBounce`, m/s. The idle pogo
   * touches down at exactly `IDLE_BOUNCE_SPEED` four times a second; without a
   * threshold that is an audio thump on a loop. Real landings clear it easily —
   * a 0.3 m drop already arrives at 3.6 m/s.
   */
  BOUNCE_EVENT_MIN: 3.5,

  // --- charge ---
  /** Seconds of holding to reach full charge. */
  CHARGE_TIME: 0.6,
  /** Charge bleeds off at this rate once released, 1/s. */
  CHARGE_DECAY: 3,

  // --- limits ---
  /** Terminal fall speed, m/s. */
  TERMINAL_V: 45,
  /** Horizontal speed above which quadratic drag kicks in, m/s. */
  MAX_H_SPEED: 26,
  /** Drag coefficient applied to the speed *above* `MAX_H_SPEED`. */
  H_DRAG: 0.5,
  /**
   * Horizontal acceleration (m/s²) at *full* lean while airborne, scaling
   * linearly with lean depth. A real tool, not a nudge: since a jump only fires
   * when you let go of the button, the flight that follows is the one place you
   * still have any say, and steering it is half of clearing a gap. A planted
   * body never reaches this code at all — it is frozen; lean there only aims.
   */
  AIR_STEER: 12,

  // --- surfaces ---
  /** Body-sphere bounce off walls. */
  WALL_RESTITUTION: 0.25,
  /** Tangential velocity kept when the body scrapes a wall. */
  WALL_FRICTION: 0.9,
  /** Surfaces steeper than this slide instead of launching, radians. */
  SLIDE_ANGLE: 50 * DEG,
  /** Bounce speed multiplier on a sliding surface. */
  SLIDE_SPEED_MUL: 0.35,
  /** Tangential velocity kept on a sliding surface (you keep falling down it). */
  SLIDE_TANGENT_KEEP: 0.92,
};

/** Separation pushed beyond a surface after any resolve, metres. */
const SEP_EPS = 1e-3;
/** Extra padding on the broadphase segment query. */
const QUERY_PAD = 0.05;
/** Depenetration iterations per substep. */
const MAX_DEPEN_ITERS = 4;
/** Overlap under which a substep counts as clean, metres. */
const STUCK_EPS = 0.02;
/** Substeps of unresolved overlap before the escape hatch fires (0.25 s). */
const STUCK_STEPS = 30;
/** Escape shoves before the body is declared unrecoverable and respawned. */
const MAX_ESCAPES = 3;

/** One frame of player intent. Produced by `core/input.ts`. */
export interface FrameInput {
  /** Strafe axis in `[-1, 1]`; `+1` is camera-right. */
  leanX: number;
  /** Forward axis in `[-1, 1]`; `+1` is away from the camera. */
  leanZ: number;
  /**
   * Space or LMB is held. Held *at the moment of landing* is what plants;
   * holding builds charge; the held→released *edge* fires the jump.
   */
  charging: boolean;
}

/**
 * Emitted once per jump, and once per landing that arrived faster than
 * `BOUNCE_EVENT_MIN`. Drives audio, screen shake and the fall toast.
 *
 * The threshold is the whole reason the idle pogo is bearable: a body left alone
 * bounces four times a second forever, and every one of those touchdowns is
 * silent. Anything you actually fell from rings through.
 */
export interface BounceEvent {
  /** Launch speed for a jump or a slide, impact speed for a landing, m/s. */
  speed: number;
  /** Surface slope from horizontal, radians. `> SLIDE_ANGLE` means it slid. */
  slope: number;
  /** How far below the last apex the landing was, metres. Always 0 for a jump. */
  fallDistance: number;
  /** Material of what was landed on, or of what is being jumped off. */
  material: Material;
  /** World contact point. A fresh vector per event; safe to keep. */
  point: Vector3;
}

// --- dev asserts -----------------------------------------------------------

function detectDev(): boolean {
  try {
    // Vite defines `import.meta.env`; under plain Node (tsx self-tests) it is absent.
    return typeof import.meta.env !== 'undefined' && import.meta.env.DEV === true;
  } catch {
    return false;
  }
}

let ASSERTS = detectDev();

/** Force the per-substep finite check on or off (the self-test turns it on). */
export function setPhysicsAsserts(on: boolean): void {
  ASSERTS = on;
}

// --- module scratch (single-threaded, never re-entered) ---------------------

const _prevTip = new Vector3();
const _newTip = new Vector3();
const _tip = new Vector3();
const _qMin = new Vector3();
const _qMax = new Vector3();
const _dir = new Vector3();
const _tan = new Vector3();
const _tanV = new Vector3();
const _wn = new Vector3();
const _head = new Vector3();
const _pn = new Vector3();
const _anyN = new Vector3();
const _toi = new Vector3();
const _endRel = new Vector3();
/** Surface velocity of whatever is being touched — zero for static geometry. */
const _pv = new Vector3();
/** Body velocity in that surface's frame. Every contact response is composed here. */
const _vRel = new Vector3();
const _beforeCarry = new Vector3();
const _dq = new Quaternion();
const _contact: ContactHit = {
  point: new Vector3(),
  normal: new Vector3(0, 1, 0),
  dist: Infinity,
  prim: undefined as unknown as Primitive,
};

// Results of the last {@link PogoBody.scanOverlap}. Module-level for the same
// reason the vectors above are: a substep must not allocate.
/** Deepest overlap of either sphere against *any* primitive; normal in `_anyN`. */
let _anyDepth = 0;
/** …restricted to primitives not yet pushed out of this substep; normal in `_wn`. */
let _freshDepth = 0;
/** The primitive `_freshDepth` belongs to, or null when nothing fresh overlaps. */
let _freshPrim: Primitive | null = null;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * World-space stick up-axis for a camera-relative lean and a yaw.
 *
 * Exported so ghosts reconstruct exactly the same axis from `PlayerState.l` and
 * `PlayerState.y` that the owning client simulated — one implementation, no
 * chance of the two drifting apart.
 *
 * The camera frame matches three.js: at yaw `θ`, forward is `(−sinθ, 0, −cosθ)`
 * and right is `(cosθ, 0, −sinθ)`.
 */
export function stickUpFromLean(leanX: number, leanZ: number, yaw: number, out: Vector3): Vector3 {
  let mag = Math.hypot(leanX, leanZ);
  if (mag < 1e-6) return out.set(0, 1, 0);
  let lx = leanX;
  let lz = leanZ;
  if (mag > 1) {
    lx /= mag;
    lz /= mag;
    mag = 1;
  }
  const angle = mag * TUNING.MAX_LEAN;
  const s = Math.sin(angle);
  const c = Math.cos(angle);
  const sy = Math.sin(yaw);
  const cy = Math.cos(yaw);
  // right·lx + forward·lz, renormalised to a unit horizontal direction.
  const dx = (lx * cy - lz * sy) / mag;
  const dz = (-lx * sy - lz * cy) / mag;
  return out.set(dx * s, c, dz * s);
}

/**
 * The player's physics body.
 *
 * One instance per client; ghosts are pure render and never own one.
 */
export class PogoBody {
  /** Body sphere centre. Live reference — read it, do not reassign it. */
  readonly pos = new Vector3();
  /** Velocity, m/s. Live reference. */
  readonly vel = new Vector3();
  /** Unit up-axis of the stick. Live reference. */
  readonly stickUp = new Vector3(0, 1, 0);

  /** True while the player is on or a hair above a surface. */
  grounded = false;
  /**
   * True while the stick is planted on a surface, waiting for the release. Only
   * ever set by landing with the button already held; while it holds, the body
   * is frozen solid — see `update`.
   */
  planted = false;
  /** Charge in `[0, 1]`. Survives a landing; spent by the jump. */
  charge = 0;
  /** Seconds since the last landing. */
  airTime = 0;
  /** Highest `pos.y` reached since the last landing — the fall-distance baseline. */
  apexY = 0;
  /** Spring compression in `[0, 1]`: 1 at impact, decaying after. */
  compression = 0;
  /** Facing, radians. Follows the camera. */
  yaw = 0;
  /** Current camera-relative lean, as sent on the wire. */
  leanX = 0;
  leanZ = 0;
  /** Speed of the most recent bounce, m/s (debug overlay). */
  lastBounceSpeed = 0;
  /**
   * How many times the wedge escape hatch has fired this session.
   *
   * A diagnostic, and a contract: in normal play this must stay at zero. The
   * self-test asserts it across a soak on the real course, because the hatch
   * teleports the body half a radius and that is only ever acceptable as the
   * alternative to being stuck forever. Survives `respawn` on purpose.
   */
  escapes = 0;

  /**
   * Contact ledger: every resolved tip contact, including the quiet ones.
   *
   * `BounceEvent` deliberately skips landings under `BOUNCE_EVENT_MIN`, but some
   * consumers must see every touchdown — checkpoint claiming cannot miss a pad
   * just because the player arrived gently. `landingSeq` increments once per
   * contact; the point and material describe the latest one. The point is a
   * reused vector — read it, do not retain it.
   */
  landingSeq = 0;
  readonly lastLandingPoint = new Vector3();
  lastLandingMaterial: Material | null = null;

  /** Landing callback. Fired from inside the substep. */
  onBounce: ((e: BounceEvent) => void) | null = null;

  private readonly level: LevelData;
  private readonly bp: Broadphase;
  /** The level's moving parts, or null on a level that has none. */
  private readonly movers: MoverSystem | null;
  /** Where `respawn()` puts you. Owned values, never an alias of the caller's. */
  private readonly respawnPoint: V3 = [0, 0, 0];
  private charging = false;
  private contactCooldown = 0;
  /** Consecutive substeps that ended with a sphere still overlapping. */
  private stuckSteps = 0;
  /** Escape shoves since the last clean substep. */
  private escapeAttempts = 0;

  // The contact we are planted on, captured at the plant and refreshed every
  // substep we hold it. `jump()` launches off this, not off whatever the tip
  // happens to be touching at the moment the button comes up.
  private readonly plantNormal = new Vector3(0, 1, 0);
  private plantMaterial: Material | null = null;
  private plantSlope = 0;
  /**
   * The primitive the plant is standing *on*, when it moves.
   *
   * Only ever non-null while `planted`. It is what the freeze block rides, and
   * what the jump inherits momentum from — the material and normal alone cannot
   * say which platform they came off, and a moving one has to be named.
   */
  private plantPrim: Primitive | null = null;

  // Previous-substep snapshot, for render interpolation.
  private readonly prevPos = new Vector3();
  private readonly prevStickUp = new Vector3(0, 1, 0);
  private prevCompression = 0;

  /** Primitives already resolved this substep, so one wall cannot be pushed twice. */
  private readonly resolved: Primitive[] = [];
  private readonly pose: SkeletonPose = {
    position: new Vector3(),
    yaw: 0,
    stickDir: new Vector3(0, 1, 0),
    compression: 0,
    charge: 0,
    verticalVel: 0,
    anim: ANIM_BOUNCE,
    timeSec: 0,
  };
  private readonly netState: PlayerState = {
    p: [0, 0, 0],
    l: [0, 0],
    y: 0,
    a: ANIM_BOUNCE,
    c: 0,
    v: 0,
  };
  private blownUp = false;

  constructor(level: LevelData, bp: Broadphase, movers?: MoverSystem) {
    this.level = level;
    this.bp = bp;
    this.movers = movers ?? null;
    this.setRespawn(level.spawn);
    this.respawn();
  }

  /** Claim a checkpoint. The values are copied, so the caller keeps its array. */
  setRespawn(p: V3): void {
    this.respawnPoint[0] = p[0];
    this.respawnPoint[1] = p[1];
    this.respawnPoint[2] = p[2];
  }

  /** Give the checkpoints back: a restart starts at the bottom, like everyone. */
  clearRespawn(): void {
    this.setRespawn(this.level.spawn);
  }

  /**
   * Reset to the active checkpoint — the plaza spawn until one is claimed — with
   * zero velocity, zero lean, zero charge.
   */
  respawn(): void {
    const s: V3 = this.respawnPoint;
    this.pos.set(s[0], s[1], s[2]);
    this.prevPos.copy(this.pos);
    this.vel.set(0, 0, 0);
    this.stickUp.set(0, 1, 0);
    this.prevStickUp.set(0, 1, 0);
    this.leanX = 0;
    this.leanZ = 0;
    this.charge = 0;
    this.charging = false;
    this.compression = 0;
    this.prevCompression = 0;
    this.contactCooldown = 0;
    this.stuckSteps = 0;
    this.escapeAttempts = 0;
    this.airTime = 0;
    this.apexY = this.pos.y;
    this.grounded = false;
    this.planted = false;
    this.plantNormal.set(0, 1, 0);
    this.plantMaterial = null;
    this.plantSlope = 0;
    this.plantPrim = null;
    // `landingSeq` stays: a respawn is not a landing, and consumers key on the
    // sequence changing, not on its value. The stale material must go, though.
    this.lastLandingMaterial = null;
    this.lastBounceSpeed = 0;
  }

  /**
   * Forget that the button is held, without firing the jump.
   *
   * For input that *vanishes* rather than being released — pause, pointer-lock
   * loss, alt-tab. Those hand `update` a neutral input, which would otherwise
   * read as a held→released edge and launch a planted player nowhere they chose.
   * A plant does not survive it either: `update` unplants a body whose button is
   * no longer down, so it simply goes back to bouncing. The charge itself is
   * left alone; it decays on the following substeps.
   */
  cancelCharge(): void {
    this.charging = false;
  }

  /** Leave a plant without launching. The ride, if there was one, ends here. */
  private unplant(): void {
    this.planted = false;
    this.plantPrim = null;
  }

  /**
   * Advance exactly one fixed substep.
   *
   * @param dt substep length; always `TUNING.FIXED_DT` in the real loop.
   * @param input player intent for this frame (the same value across substeps).
   * @param camYaw camera yaw, which defines the frame the lean axes live in.
   */
  update(dt: number, input: FrameInput, camYaw: number): void {
    const T = TUNING;
    this.yaw = camYaw;
    this.prevPos.copy(this.pos);
    this.prevStickUp.copy(this.stickUp);
    this.prevCompression = this.compression;

    // 1. release edge, then charge integrate ---------------------------------
    // The jump fires on the held→released edge and only while planted; letting
    // go in mid-air just starts the decay. It fires *before* the decay below so
    // a full hold pays out the whole CHARGE_GAIN instead of one substep less.
    const released = this.charging && !input.charging;
    this.charging = input.charging;
    if (released && this.planted) this.jump();
    if (input.charging) this.charge = Math.min(1, this.charge + dt / T.CHARGE_TIME);
    else this.charge = Math.max(0, this.charge - T.CHARGE_DECAY * dt);

    // 2. lean slew -----------------------------------------------------------
    // Exponential approach rather than a hard ramp: an instant lean would let you
    // re-aim on the impact frame and cheat every bounce.
    let tx = input.leanX;
    let tz = input.leanZ;
    const tm = Math.hypot(tx, tz);
    if (tm > 1) {
      tx /= tm;
      tz /= tm;
    }
    const k = 1 - Math.exp(-T.LEAN_RATE * dt);
    this.leanX += (tx - this.leanX) * k;
    this.leanZ += (tz - this.leanZ) * k;
    stickUpFromLean(this.leanX, this.leanZ, camYaw, this.stickUp);

    // 3. planted freeze ------------------------------------------------------
    // A plant only lives as long as the button does. The release edge above
    // already fired the jump in the normal case; this catches everything else —
    // `cancelCharge` on a pause or an alt-tab, or an input that simply stopped
    // arriving — by dropping the plant and letting the body bounce again.
    if (this.planted && !input.charging) this.unplant();

    // 3b. riding a mover ------------------------------------------------------
    // Frozen is frozen *relative to the platform*, not to the world. The carry is
    // the platform's exact delta transform for this tick rather than v·dt, so a
    // minute on a carousel accumulates no drift at all, and only the contact
    // normal turns with it — the stick's aim belongs to the player, not to the
    // ride. A blink platform that vanishes underneath simply drops the plant, and
    // the substep continues as a fall.
    if (this.planted && this.plantPrim !== null && this.movers !== null) {
      if (!this.movers.isActive(this.plantPrim)) {
        this.unplant();
      } else {
        _beforeCarry.copy(this.pos);
        this.movers.carryPoint(this.plantPrim, this.pos);
        if (this.movers.deltaRotation(this.plantPrim, _dq)) {
          this.plantNormal.applyQuaternion(_dq);
        }
        // A frozen body used to be unpushable, which is what let the freeze skip
        // depenetration entirely. Carried, it is not: a platform can drive a
        // rider into a wall, and the ride has to give way rather than bury it.
        if (!this.pos.equals(_beforeCarry)) this.depenetrate();
      }
    }

    // Planted, the body does not move. At all. The render rig pivots the lean at
    // the *body* (root at pose.position, stick swinging beneath it), so physics
    // has to pin the same point: the old code re-snapped the body up the tilted
    // stick every substep, which swung it sideways and downward around the
    // planted tip — up to a quarter of a metre at full lean — and read, exactly
    // and unforgivably, as falling over while you were still aiming. Aiming must
    // never move you. Skipping the sweep is also what makes the plant safe: the
    // substep that planted still ran its depenetration, so a frozen body is
    // never frozen inside geometry.
    if (this.planted) {
      if (this.compression > 0) {
        this.compression = Math.max(0, this.compression - T.COMPRESSION_DECAY * dt);
      }
      this.airTime = 0;
      this.grounded = true;
      if (this.contactCooldown > 0) this.contactCooldown -= dt;
      if (ASSERTS) this.assertFinite();
      return;
    }

    // 4. gravity + terminal clamp -------------------------------------------
    this.vel.y += T.GRAVITY * dt;
    if (this.vel.y < -T.TERMINAL_V) this.vel.y = -T.TERMINAL_V;

    // 4b. air steer — proportional to lean depth, so a tap nudges and a full
    // lean pulls at exactly AIR_STEER. The stick's horizontal component maxes
    // out at sin(MAX_LEAN), which is what normalises the scale here.
    if (T.AIR_STEER !== 0) {
      const hx = this.stickUp.x;
      const hz = this.stickUp.z;
      if (Math.hypot(hx, hz) > 1e-6) {
        const a = (T.AIR_STEER * dt) / Math.sin(T.MAX_LEAN);
        this.vel.x += hx * a;
        this.vel.z += hz * a;
      }
    }

    // 5. soft horizontal drag ------------------------------------------------
    const h = Math.hypot(this.vel.x, this.vel.z);
    if (h > T.MAX_H_SPEED) {
      const excess = h - T.MAX_H_SPEED;
      const nh = Math.max(T.MAX_H_SPEED, h - T.H_DRAG * excess * excess * dt);
      const s = nh / h;
      this.vel.x *= s;
      this.vel.z *= s;
    }

    // 6. integrate -----------------------------------------------------------
    this.pos.addScaledVector(this.vel, dt);

    // 7. tip sweep first — the tip beats the body, so ledge edges catch --------
    // This is where a plant is made; the depenetration below still runs on it in
    // this same substep, so the freeze above only ever starts from a
    // depenetrated position.
    this.sweepTip();

    // 8. body sweep — the anti-tunnel guard, on the post-bounce position -------
    this.sweepBody();

    // 9. body + head depenetration for walls ---------------------------------
    this.depenetrate();

    // 10. bookkeeping ---------------------------------------------------------
    this.airTime += dt;
    if (this.contactCooldown > 0) this.contactCooldown -= dt;
    if (this.compression > 0) {
      this.compression = Math.max(0, this.compression - T.COMPRESSION_DECAY * dt);
    }
    if (this.pos.y > this.apexY) this.apexY = this.pos.y;
    this.grounded = this.planted || this.airTime < T.GROUND_TIME;

    if (ASSERTS) this.assertFinite();
  }

  // --- collision ----------------------------------------------------------

  /**
   * World velocity of `prim`'s surface at `point`, written into `_pv`.
   *
   * Every contact response is composed in this frame and has `_pv` added back at
   * the end, which is the whole of "moving platforms" as far as the physics is
   * concerned: static geometry answers zero and the maths collapses to exactly
   * what it always was.
   *
   * @returns `_pv` — module scratch. Read it, do not retain it.
   */
  private platformVel(prim: Primitive, point: Vector3): Vector3 {
    if (this.movers === null) return _pv.set(0, 0, 0);
    return this.movers.velocityAt(prim, point, _pv);
  }

  /**
   * What a *body* or *head* contact does — the one response both the sweep and
   * the depenetration give, in the surface's own frame.
   *
   * Kill the inward relative component (plus a little `WALL_RESTITUTION`), scuff
   * what is left along the surface by `WALL_FRICTION`, and add the surface's own
   * motion back at the end. Composing it relative to `pv` is the whole of what
   * makes a moving wall *shove*: the body comes out of it travelling with the
   * wall, instead of the wall merely refusing to be entered.
   *
   * The one asymmetry is the floor on a **standable** normal, and it exists
   * because of a specific way a player used to be lost for good. The stick is
   * 1.15 m long and the platforms are 0.5 m thick, so a side entry can leave the
   * character *straddling* a plate — tip below the underside, body sphere above
   * the top face. Only the tip bounces or plants ({@link resolveBounce}); a tip
   * hanging in free air under the plate is a tip that will never touch anything
   * again. The body, meanwhile, is standing on the top face getting this
   * response, and with restitution below 1 that decays to a dead stop in a
   * handful of substeps. The result was a body permanently at rest, lying on a
   * platform with the stick threaded through it, and nothing the player could
   * press would move it: no tip contact means no bounce, no plant, no jump.
   *
   * So a body resting on something it could have stood on rebounds at no less
   * than `IDLE_BOUNCE_SPEED` — the same floor, and for the same reason, as the
   * one that turns the tip's decaying auto-bounce into a perpetual little pogo.
   * An un-planted body must never be at rest; a pogo idles by bouncing. It is a
   * floor and not a target, so a contact already leaving faster keeps its speed.
   * Walls, undersides and anything past `SLIDE_ANGLE` are untouched by it: there
   * is nothing to stand on there, and a rebound off a wall is not an idle.
   *
   * @param n unit contact normal, pointing out of the surface.
   * @param pv surface velocity at the contact — the `_pv` scratch from
   *   {@link platformVel}, read but never written.
   */
  private wallResponse(n: Vector3, pv: Vector3): void {
    const T = TUNING;
    _vRel.copy(this.vel).sub(pv);
    const vn = _vRel.dot(n);
    if (vn >= 0) return;
    _vRel.addScaledVector(n, -vn * (1 + T.WALL_RESTITUTION));
    let outward = _vRel.dot(n);
    _tanV.copy(_vRel).addScaledVector(n, -outward).multiplyScalar(T.WALL_FRICTION);
    if (n.y >= Math.cos(T.SLIDE_ANGLE) && outward < T.IDLE_BOUNCE_SPEED) {
      outward = T.IDLE_BOUNCE_SPEED;
    }
    this.vel.copy(_tanV).addScaledVector(n, outward).add(pv);
  }

  /** Sweep the stick tip along its path this substep and resolve the first hit. */
  private sweepTip(): void {
    const T = TUNING;
    _prevTip.copy(this.prevPos).addScaledVector(this.stickUp, -T.STICK_LENGTH);
    _newTip.copy(this.pos).addScaledVector(this.stickUp, -T.STICK_LENGTH);

    const cands = this.bp.querySegment(_prevTip, _newTip, T.TIP_RADIUS + QUERY_PAD);
    let best: SweepHit | null = null;
    for (let i = 0; i < cands.length; i++) {
      const hit = sweepSphere(cands[i]!, _prevTip, _newTip, T.TIP_RADIUS);
      if (hit !== null && (best === null || hit.t < best.t)) best = hit;
    }
    // A planted body never gets here — `update` returns before the sweep — so
    // this only ever runs airborne, and `resolveBounce` is always entered
    // unplanted.
    if (best !== null) this.resolveBounce(best);
  }

  /**
   * Sweep the body sphere along the path it actually travelled this substep.
   *
   * The tip sweep only protects the tip. The body was never swept at all — it was
   * only ever *depenetrated*, which is a discrete test and can therefore only
   * rescue a body that ends the substep inside something. At `TERMINAL_V` the
   * centre moves 0.375 m per substep, so a body that starts above a thin platform
   * and ends below it is inside nothing at either end, and nothing ever sees it.
   * This is the guard against precisely that, and nothing else.
   *
   * It runs *after* `sweepTip`, from `prevPos` to the **post-bounce** `pos`,
   * because a plantable landing re-hangs the body up the stick: sweeping to the
   * pre-landing position would resolve against a state that no longer exists and
   * fight the very landing it is meant to protect. Three guards keep it corrective
   * rather than clever, and together they mean it never fires on a normal contact:
   *
   * - `planted` bodies are frozen and already depenetrated — leave them alone;
   * - a hit the velocity is *leaving* (`v·n ≥ 0`) is a graze on the way out, or
   *   the launch a landing just paid out, and snapping it back would eat it;
   * - a path that clips a corner and comes out clear on the far side is not a
   *   tunnel. Only an end state still short of the contact plane is corrected.
   *
   * A `t = 0` hit means the body *started* the substep overlapping, which is
   * `depenetrate`'s job; resolving it here as well would push twice.
   */
  private sweepBody(): void {
    const T = TUNING;
    const R = T.BODY_RADIUS;
    if (this.planted) return;

    const cands = this.bp.querySegment(this.prevPos, this.pos, R + QUERY_PAD);
    let best: SweepHit | null = null;
    for (let i = 0; i < cands.length; i++) {
      const hit = sweepSphere(cands[i]!, this.prevPos, this.pos, R);
      if (hit !== null && (best === null || hit.t < best.t)) best = hit;
    }
    if (best === null || best.t <= 0) return;

    const n = best.normal;
    // Relative to the surface, so a sweeper bar closing on a stationary body
    // still counts as an approach rather than as a graze it is leaving.
    const pv = this.platformVel(best.prim, best.point);
    _vRel.copy(this.vel).sub(pv);
    if (_vRel.dot(n) >= 0) return;

    // Cleanly outside the primitive *and* on the outward side of the contact
    // plane: it grazed past. Anything else — still overlapping, or out the far
    // side, which is the tunnel — gets pulled back to the time of impact.
    const end = closestPoint(best.prim, this.pos, _contact);
    _endRel.copy(this.pos).sub(best.point);
    if (end.dist >= R && _endRel.dot(n) > 0) return;

    _toi.lerpVectors(this.prevPos, this.pos, best.t).addScaledVector(n, SEP_EPS);
    this.pos.copy(_toi);

    // The same response `depenetrate` gives a discrete overlap — one method, so
    // a swept contact and an overlapping one can never disagree about what
    // touching a wall does. It recomputes `_vRel`, which is free and keeps the
    // response readable in one place.
    this.wallResponse(n, pv);
  }

  /**
   * The minimum move that puts the tip back outside the surface it hit.
   *
   * Pushes the *body* along the contact normal by however much the tip is short
   * of `TIP_RADIUS + SEP_EPS` clearance, and changes nothing else. This is the
   * correction for every contact you cannot stand on — see {@link resolveBounce}
   * for why the full tip-snap is not.
   */
  private separateTip(hit: SweepHit, n: Vector3): void {
    const T = TUNING;
    _tip.copy(this.pos).addScaledVector(this.stickUp, -T.STICK_LENGTH).sub(hit.point);
    const needed = T.TIP_RADIUS + SEP_EPS - _tip.dot(n);
    if (needed > 0) this.pos.addScaledVector(n, needed);
  }

  /**
   * What a tip contact does — the whole of the game feel, in three cases.
   *
   * On anything you can stand on (slope ≤ `SLIDE_ANGLE`) the button decides.
   * **Held**, the contact *plants*: velocity dies outright, the body freezes
   * where it stands and nothing launches until the player lets go
   * ({@link jump}). The charge is deliberately *not* consumed — carrying a hold
   * through the landing and spending it on the release is the whole mechanic.
   * **Up**, the contact *auto-bounces*: `BOUNCE_RESTITUTION` of the impact speed
   * comes straight back along {@link launchDir}, floored at
   * `IDLE_BOUNCE_SPEED`. Restitution below 1 means each rebound is smaller than
   * the last, so a big fall decays over a handful of bounces into the floor and
   * stays there — a pogo that idles rather than a body that stops. The floor is
   * a floor, not a target: it never adds energy to a bounce already above it.
   *
   * Anything steeper **slides**, and slides behave exactly as they always did:
   * `BASE_BOUNCE + IMPACT_RETENTION · (impact − BASE_BOUNCE)⁺ + CHARGE_GAIN ·
   * charge`, scaled by `SLIDE_SPEED_MUL`, keeping most of the tangential
   * velocity. Note the impact term measures speed in *excess* of the natural
   * bounce: retaining a fraction of the raw impact speed instead is a positive
   * feedback loop (`v = 6.5 + 0.35v` settles at 10 m/s and climbs from the first
   * frame). Funnels do not launch you. They feed you to the abyss.
   *
   * Positional correction is per-branch, not shared, and that is load-bearing.
   * Hanging the body off the top of the stick — the plantable snap below — only
   * means anything when the stick is *standing* on something. Do it against a
   * wall or a box edge and each contact hoists the body a little further up the
   * tilted stick; a few substeps of that and the player is wedged in the
   * geometry with no way out. Everywhere else gets the minimum push along the
   * normal and nothing more ({@link separateTip}).
   */
  private resolveBounce(hit: SweepHit): void {
    const T = TUNING;
    const n = hit.normal;
    // Everything below is measured against the *surface*, not the world: an
    // elevator rising to meet you hits as hard as you falling onto it, and a
    // ferry you land on square must not read as a sideways scrape.
    const pv = this.platformVel(hit.prim, hit.point);
    _vRel.copy(this.vel).sub(pv);
    const vn = _vRel.dot(n);

    // Already separating: the sweep caught a graze on the way out.
    if (vn > 0.5) return;

    // Inside the cooldown this is the same contact we just launched off: hold the
    // surface, but never pay out a second impulse.
    if (this.contactCooldown > 0) {
      this.separateTip(hit, n);
      if (vn < 0) this.vel.addScaledVector(n, -vn);
      return;
    }

    const mat = hit.prim.material;
    const friction = mat.frictionMultiplier ?? 1;
    const bounceMul = mat.bounceMultiplier ?? 1;
    const slope = Math.acos(clamp(n.y, -1, 1));
    const impact = Math.max(0, -vn);

    // The ledger records every contact past the cooldown, quiet or loud —
    // consumers that must not miss a gentle touchdown poll this, not onBounce.
    this.landingSeq++;
    this.lastLandingPoint.copy(hit.point);
    this.lastLandingMaterial = mat;

    if (slope > T.SLIDE_ANGLE) {
      // --- too steep to plant: deflect and keep falling down it --------------
      this.separateTip(hit, n);

      let speed =
        T.BASE_BOUNCE +
        T.IMPACT_RETENTION * Math.max(0, impact - T.BASE_BOUNCE) +
        T.CHARGE_GAIN * this.charge;
      speed *= bounceMul;
      speed *= T.SLIDE_SPEED_MUL;
      speed = clamp(speed, T.MIN_BOUNCE_SPEED, T.MAX_BOUNCE_SPEED * bounceMul);
      const keep = T.SLIDE_TANGENT_KEEP * friction;

      // Compose in the surface's frame: retained tangential + the new launch,
      // and the surface's own motion added back at the end.
      _tanV.copy(_vRel).addScaledVector(n, -vn);
      this.vel.copy(_tanV).multiplyScalar(keep).addScaledVector(this.launchDir(n), speed).add(pv);

      const fallDistance = Math.max(0, this.apexY - this.pos.y);
      this.charge = 0;
      this.compression = 1;
      this.contactCooldown = T.CONTACT_COOLDOWN;
      this.airTime = 0;
      this.apexY = this.pos.y;
      this.grounded = true;
      this.lastBounceSpeed = speed;

      if (this.onBounce !== null) {
        // One allocation per landing (~1 Hz), never per substep.
        this.onBounce({ speed, slope, fallDistance, material: mat, point: hit.point });
      }
      return;
    }

    // --- plantable: put the tip on the surface and hang the body off it ------
    this.pos
      .copy(hit.point)
      .addScaledVector(n, T.TIP_RADIUS + SEP_EPS)
      .addScaledVector(this.stickUp, T.STICK_LENGTH);

    const fallDistance = Math.max(0, this.apexY - this.pos.y);
    // Squash scales with the arrival: the idle pogo's gentle touchdown gets a
    // light one, anything landing at BASE_BOUNCE or harder still bottoms out.
    this.compression = clamp(impact / T.BASE_BOUNCE, 0.35, 1);
    this.airTime = 0;
    this.apexY = this.pos.y;
    this.grounded = true;

    if (this.charging) {
      // --- button held on touchdown: plant ----------------------------------
      // Dead stop, not a scuff. The body is about to be frozen for as long as the
      // hold lasts, so any surviving velocity is velocity that would be silently
      // thrown away at the release — better it never existed.
      this.vel.set(0, 0, 0);
      this.planted = true;
      this.plantNormal.copy(n);
      this.plantMaterial = mat;
      this.plantSlope = slope;
      // Naming the primitive is what lets the freeze ride it — a plant on a
      // moving platform is frozen relative to the platform, not to the world.
      this.plantPrim = hit.prim;
      this.contactCooldown = 0;
    } else {
      // --- button up: bounce like a ball ------------------------------------
      // `launchDir` and not straight up, because aiming the rebound with the
      // stick *is* the locomotion. At zero lean on flat ground it comes out
      // exactly vertical, which is what keeps the idle pogo on the spot.
      const speed = clamp(
        T.BOUNCE_RESTITUTION * impact * bounceMul,
        T.IDLE_BOUNCE_SPEED,
        T.MAX_BOUNCE_SPEED * bounceMul,
      );
      _tanV.copy(_vRel).addScaledVector(n, -vn);
      this.vel
        .copy(_tanV)
        .multiplyScalar(T.TANGENT_KEEP * friction)
        .addScaledVector(this.launchDir(n), speed)
        .add(pv);
      this.contactCooldown = T.CONTACT_COOLDOWN;
      this.lastBounceSpeed = speed;
      // The charge is untouched: it builds only while held, and decays otherwise.
      // Bouncing through a landing must not cost you a hold you never released.
    }

    if (impact > T.BOUNCE_EVENT_MIN && this.onBounce !== null) {
      // One allocation per audible landing (~1 Hz), never per substep. Below the
      // threshold nothing is emitted at all — the idle pogo has to be silent.
      this.onBounce({ speed: impact, slope, fallDistance, material: mat, point: hit.point });
    }
  }

  /**
   * Launch direction off a surface with normal `n`, written into `_dir`.
   *
   * Mostly the stick axis, not the surface normal — pure reflection reads as a
   * bouncing ball, and the whole point of a pogo stick is that where you point it
   * is where you go. The `MIN_BOUNCE_DOT` guard keeps a wild lean from firing you
   * into the surface, and because it holds, the launch always separates: a jump
   * cannot immediately re-trigger the contact it came from.
   *
   * @returns `_dir` — module scratch. Read it, do not retain it.
   */
  private launchDir(n: Vector3): Vector3 {
    const T = TUNING;
    _dir
      .copy(n)
      .multiplyScalar(1 - T.LEAN_AUTHORITY)
      .addScaledVector(this.stickUp, T.LEAN_AUTHORITY);
    if (_dir.lengthSq() < 1e-8) _dir.copy(n);
    else _dir.normalize();
    const d = _dir.dot(n);
    if (d < T.MIN_BOUNCE_DOT) {
      _tan.copy(_dir).addScaledVector(n, -d);
      const tl = _tan.length();
      if (tl < 1e-6) {
        _dir.copy(n);
      } else {
        // Keep the tangential aim, raise the elevation to exactly MIN_BOUNCE_DOT.
        const md = T.MIN_BOUNCE_DOT;
        _tan.multiplyScalar(1 / tl);
        _dir.copy(n).multiplyScalar(md).addScaledVector(_tan, Math.sqrt(1 - md * md));
      }
    }
    return _dir;
  }

  /**
   * Spend the charge and leave the surface. Fired by the held→released edge of
   * `FrameInput.charging`, and only while planted.
   *
   * Magnitude is purely `BASE_BOUNCE + CHARGE_GAIN · charge` — a tap gets the
   * floor, a full hold gets the ceiling, and nothing in between depends on how
   * hard you arrived. The landing's momentum was already spent at the plant, so
   * there is no impact term to fold back in: how high you go is a decision, not
   * an accident of the last bounce.
   */
  private jump(): void {
    const T = TUNING;
    const mat = this.plantMaterial;
    // `planted` always carries a material; the guard is for the type, not a case.
    if (mat === null) {
      this.unplant();
      return;
    }
    const n = this.plantNormal;
    const bounceMul = mat.bounceMultiplier ?? 1;

    let speed = (T.BASE_BOUNCE + T.CHARGE_GAIN * this.charge) * bounceMul;
    speed = clamp(speed, T.MIN_BOUNCE_SPEED, T.MAX_BOUNCE_SPEED * bounceMul);

    // The plant zeroed the velocity and the freeze kept it that way, so this is
    // the launch alone. The composition survives for the day a plant is allowed
    // to keep some drift: only the inward component is dropped, so the launch is
    // never eaten by the surface.
    const vn = this.vel.dot(n);
    _tanV.copy(this.vel);
    if (vn < 0) _tanV.addScaledVector(n, -vn);
    this.vel.copy(_tanV).addScaledVector(this.launchDir(n), speed);

    // Momentum inheritance: the launch is composed in the platform's frame, so
    // stepping off a rising elevator carries its climb and a ferry throws you the
    // way it was already going. Sampled at the tip, which is what is touching it.
    if (this.plantPrim !== null && this.movers !== null) {
      _tip.copy(this.pos).addScaledVector(this.stickUp, -T.STICK_LENGTH);
      this.vel.add(this.movers.velocityAt(this.plantPrim, _tip, _pv));
    }

    // No compression pop here: the squash belongs to the plant, which already
    // fired it. This is the spring letting go.
    this.charge = 0;
    this.unplant();
    this.contactCooldown = T.CONTACT_COOLDOWN;
    this.airTime = 0;
    this.apexY = this.pos.y;
    this.lastBounceSpeed = speed;

    if (this.onBounce !== null) {
      // One allocation per jump (~1 Hz), never per substep — same budget as a
      // landing, and the event contract promises a point the caller may keep.
      const point = new Vector3()
        .copy(this.pos)
        .addScaledVector(this.stickUp, -T.STICK_LENGTH);
      this.onBounce({ speed, slope: this.plantSlope, fallDistance: 0, material: mat, point });
    }
  }

  /**
   * Deepest overlap of *either* collision sphere against the level, right now.
   *
   * Two spheres, because the character is two spheres: the body at `pos` and the
   * head a `HEAD_OFFSET` up the stick. Whichever is buried deeper wins, and the
   * whole character then translates rigidly along that one contact normal — the
   * body is not a ragdoll, and moving one sphere without the other would stretch
   * it. The AABB query covers both.
   *
   * Two results are written, because the two callers want different things.
   * `_freshDepth`/`_wn`/`_freshPrim` see only primitives this substep has not
   * already pushed out of, which is what stops one wall from being resolved twice
   * and what stops two facing walls trading the body back and forth forever.
   * `_anyDepth`/`_anyN` are the honest state of the body regardless — the only
   * thing {@link escapeWedge} can safely judge, since a body pinned between two
   * *already-resolved* walls looks perfectly clean to the filtered view.
   */
  private scanOverlap(): void {
    const T = TUNING;
    const R = T.BODY_RADIUS;
    const HR = T.HEAD_RADIUS;
    _head.copy(this.pos).addScaledVector(this.stickUp, T.HEAD_OFFSET);

    _qMin.set(
      Math.min(this.pos.x - R, _head.x - HR),
      Math.min(this.pos.y - R, _head.y - HR),
      Math.min(this.pos.z - R, _head.z - HR),
    );
    _qMax.set(
      Math.max(this.pos.x + R, _head.x + HR),
      Math.max(this.pos.y + R, _head.y + HR),
      Math.max(this.pos.z + R, _head.z + HR),
    );
    const cands = this.bp.queryAABB(_qMin, _qMax);

    _anyDepth = 0;
    _freshDepth = 0;
    _freshPrim = null;
    for (let i = 0; i < cands.length; i++) {
      const prim = cands[i]!;
      let depth = R - closestPoint(prim, this.pos, _contact).dist;
      _pn.copy(_contact.normal);
      const headDepth = HR - closestPoint(prim, _head, _contact).dist;
      if (headDepth > depth) {
        depth = headDepth;
        _pn.copy(_contact.normal);
      }
      if (depth <= 0) continue;
      if (depth > _anyDepth) {
        _anyDepth = depth;
        _anyN.copy(_pn);
      }
      if (depth > _freshDepth && !this.resolved.includes(prim)) {
        _freshDepth = depth;
        _freshPrim = prim;
        _wn.copy(_pn);
      }
    }
  }

  /** Push the character out of anything it ended the substep inside. */
  private depenetrate(): void {
    this.resolved.length = 0;

    let iter = 0;
    for (; iter < MAX_DEPEN_ITERS; iter++) {
      this.scanOverlap();
      if (_freshPrim === null) break;
      this.resolved.push(_freshPrim);

      this.pos.addScaledVector(_wn, _freshDepth + SEP_EPS);
      // Sampled at the body centre rather than at the contact point: the exact
      // surface point is a radius away at most, and `scanOverlap` has already
      // reused `_contact` for whatever it looked at last.
      const pv = this.platformVel(_freshPrim, this.pos);
      this.wallResponse(_wn, pv);
    }
    // A loop that ran to its cap pushed on its last pass without re-measuring;
    // the wedge check needs the state as it actually is. A loop that broke out
    // early already scanned after its last push, so `_anyDepth` is current.
    if (iter === MAX_DEPEN_ITERS) this.scanOverlap();
    this.escapeWedge();
  }

  /**
   * Last resort for a body that depenetration cannot free.
   *
   * Iterative depenetration is a local, best-effort solver: pinned in a slot
   * narrower than the body, or wedged where two normals disagree, it can spend
   * every iteration it has and still leave a sphere buried, substep after
   * substep. There is no combination of pushes that fixes that, and the player
   * cannot see why they are not moving — so past a quarter-second of it, stop
   * being polite. Shove the body a clear half-radius beyond the deepest contact,
   * kill the inward velocity so it does not immediately fall back in, and if
   * three of those still leave it buried, the position is unrecoverable and the
   * checkpoint is the honest answer.
   *
   * This is a safety net and must read as one: the streak resets on any clean
   * substep, so nothing in ordinary bouncing, planting or wall-scraping — where
   * the solver converges within a substep or two — can ever reach it.
   */
  private escapeWedge(): void {
    if (_anyDepth <= STUCK_EPS) {
      this.stuckSteps = 0;
      this.escapeAttempts = 0;
      return;
    }
    if (++this.stuckSteps < STUCK_STEPS) return;

    this.stuckSteps = 0;
    this.escapeAttempts++;
    this.escapes++;
    this.pos.addScaledVector(_anyN, _anyDepth + TUNING.BODY_RADIUS * 0.5);
    const vn = this.vel.dot(_anyN);
    if (vn < 0) this.vel.addScaledVector(_anyN, -vn);

    this.scanOverlap();
    if (_anyDepth > STUCK_EPS && this.escapeAttempts >= MAX_ESCAPES) this.respawn();
  }

  // --- output -------------------------------------------------------------

  /**
   * Interpolated pose for rendering.
   *
   * @param alpha `[0, 1]` between the previous and current substep.
   * @param timeSec monotonic clock, for animation cycle phase.
   * @returns a reused instance — read it, do not retain it.
   */
  getPose(alpha: number, timeSec: number): SkeletonPose {
    const a = clamp(alpha, 0, 1);
    const p = this.pose;
    p.position.lerpVectors(this.prevPos, this.pos, a);
    p.stickDir.copy(this.prevStickUp).lerp(this.stickUp, a);
    if (p.stickDir.lengthSq() < 1e-8) p.stickDir.set(0, 1, 0);
    else p.stickDir.normalize();
    p.yaw = this.yaw;
    p.compression = this.prevCompression + (this.compression - this.prevCompression) * a;
    p.charge = this.charge;
    p.verticalVel = this.vel.y;
    p.anim = this.getAnim();
    p.timeSec = timeSec;
    return p;
  }

  /** Which animation the skeleton should be playing right now. */
  getAnim(): AnimState {
    if (this.charging && this.charge > 0.05) return ANIM_CHARGING;
    // Planted, but the hold is too young to read as a charge: coiled and still.
    if (this.planted) return ANIM_BOUNCE;
    if (this.vel.y < -18) return ANIM_FLAIL;
    if (this.airTime < 0.25) return ANIM_BOUNCE;
    return ANIM_AIRBORNE;
  }

  /** Wire state, rounded to 2 dp. Allocates one small object per call (12 Hz). */
  getNetState(): PlayerState {
    const s = this.netState;
    s.p[0] = this.pos.x;
    s.p[1] = this.pos.y;
    s.p[2] = this.pos.z;
    s.l[0] = this.leanX;
    s.l[1] = this.leanZ;
    s.y = this.yaw;
    s.a = this.getAnim();
    s.c = this.charge;
    s.v = this.vel.y;
    return roundState(s);
  }

  // --- dev ----------------------------------------------------------------

  private assertFinite(): void {
    const p = this.pos;
    const v = this.vel;
    if (
      Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z) &&
      Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z)
    ) {
      return;
    }
    if (!this.blownUp) {
      this.blownUp = true;
      console.error('[pogo] non-finite state — respawning', {
        pos: p.toArray(),
        vel: v.toArray(),
        stickUp: this.stickUp.toArray(),
        lean: [this.leanX, this.leanZ],
        yaw: this.yaw,
        charge: this.charge,
        planted: this.planted,
        airTime: this.airTime,
        apexY: this.apexY,
        compression: this.compression,
      });
    }
    this.respawn();
  }
}
